import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import FilterPanel, { type FilterState } from "@/pages/category/components/FilterPanel";
import { usePageMeta } from "@/hooks/usePageMeta";
import { filterMovies, getAllMovies, sortMovies, type SortKey } from "@/utils/movies";

const PAGE_SIZE = 8;

const sortOptions: { value: SortKey; label: string }[] = [
  { value: "default", label: "综合排序" },
  { value: "updated", label: "按更新时间" },
  { value: "rating", label: "按评分" },
  { value: "hot", label: "按热度" },
];

export default function Category() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  const filters: FilterState = {
    type: searchParams.get("type") === "tv" ? "tv" : "movie",
    genre: searchParams.get("genre") || "",
    region: searchParams.get("region") || "",
    year: searchParams.get("year") || "",
  };
  const sort = (searchParams.get("sort") as SortKey) || "default";

  const isTv = filters.type === "tv";
  const title = isTv ? "电视剧分类 - 星辰影视" : "电影分类 - 星辰影视";
  const description = isTv
    ? "星辰影视电视剧分类页，涵盖历史、家庭、青春、悬疑等多种剧集题材，支持按类型、地区、年份筛选与排序浏览。"
    : "星辰影视电影分类页，涵盖科幻、悬疑、爱情、动作等多种电影题材，支持按类型、地区、年份筛选与排序浏览。";
  usePageMeta(title, description, isTv ? "电视剧分类,剧集大全,星辰影视" : "电影分类,电影大全,星辰影视");

  const result = useMemo(() => {
    const filtered = filterMovies(getAllMovies(), filters);
    return sortMovies(filtered, sort);
  }, [filters.type, filters.genre, filters.region, filters.year, sort]);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [filters.type, filters.genre, filters.region, filters.year, sort]);

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key === "type") {
      next.delete("genre");
      next.delete("region");
      next.delete("year");
    }
    setSearchParams(next);
  };

  const resetFilters = () => {
    const next = new URLSearchParams();
    next.set("type", filters.type);
    setSearchParams(next);
  };

  const visible = result.slice(0, visibleCount);

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-6 py-6 md:py-10">
      <Breadcrumb items={[{ label: isTv ? "电视剧" : "电影" }]} />

      <div className="mt-5 mb-6">
        <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950">
          {isTv ? "电视剧分类" : "电影分类"}
        </h1>
        <p className="mt-2 text-sm text-foreground-600 max-w-3xl leading-relaxed">
          {isTv
            ? "按题材、地区与年份浏览电视剧集，找到值得追下去的那一部。"
            : "按题材、地区与年份浏览电影作品，快速锁定你感兴趣的方向。"}
        </p>
      </div>

      <div className="flex gap-6 lg:gap-8 items-start">
        <aside className="hidden lg:block w-64 shrink-0 sticky top-24">
          <FilterPanel
            filters={filters}
            sort={sort}
            onChange={(key, value) => updateParam(key, value)}
            onReset={resetFilters}
          />
        </aside>

        <div className="flex-1 min-w-0 w-full">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setMobileFilterOpen((v) => !v)}
                className="lg:hidden inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-800 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-filter-3-line" aria-hidden="true" />
                筛选
                <i
                  className={`ri-arrow-down-s-line transition-transform ${mobileFilterOpen ? "rotate-180" : ""}`}
                  aria-hidden="true"
                />
              </button>
              <span className="text-sm text-foreground-600">
                共 <strong className="text-primary-600 font-label">{result.length}</strong> 部作品
              </span>
            </div>

            <div className="flex items-center gap-2">
              <label htmlFor="sort-select" className="text-sm text-foreground-600 whitespace-nowrap">
                排序
              </label>
              <select
                id="sort-select"
                value={sort}
                onChange={(e) => updateParam("sort", e.target.value)}
                className="h-9 rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 cursor-pointer outline-none focus:border-primary-400"
              >
                {sortOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {mobileFilterOpen && (
            <div className="lg:hidden mb-5">
              <FilterPanel
                filters={filters}
                sort={sort}
                onChange={(key, value) => updateParam(key, value)}
                onReset={resetFilters}
              />
            </div>
          )}

          {result.length === 0 ? (
            <div className="bg-background-50 rounded-lg border border-background-200 py-16 flex flex-col items-center text-center px-4">
              <span className="w-14 h-14 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-500">
                <i className="ri-emotion-sad-line text-3xl" aria-hidden="true" />
              </span>
              <p className="mt-4 text-foreground-800 font-medium">没有找到符合条件的影视作品</p>
              <p className="mt-1.5 text-sm text-foreground-500">试试调整或重置筛选条件</p>
              <button
                type="button"
                onClick={resetFilters}
                className="mt-5 h-9 px-5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                重置筛选
              </button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 md:gap-5">
                {visible.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>

              {visibleCount < result.length && (
                <div className="flex justify-center mt-8">
                  <button
                    type="button"
                    onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                    className="inline-flex items-center gap-2 h-11 px-8 rounded-md border border-primary-300 bg-background-50 text-primary-700 hover:bg-primary-50 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-add-line" aria-hidden="true" />
                    加载更多（剩余 {result.length - visibleCount} 部）
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}